import React, { useState } from 'react';
import { Sparkles, Clock, CheckSquare, HelpCircle, Download, FileText, Share2, Mic, MicOff, Activity } from 'lucide-react';

export type AIAssistantPanelProps = {
  sessionDuration: number; // in seconds
  summary: string;
  keyPoints: string[];
  actionItems: { owner: string; task: string; deadline?: string }[];
  questions: { text: string; askedBy: string }[];
  qaHistory?: { question: string; answer: string }[]; // New prop for Q&A history
  onAskQuestion?: (question: string) => Promise<string>; // Now returns promise
  onExport?: (format: 'txt' | 'pdf') => void;
  isProcessing?: boolean;
  transcriptLength?: number; // Length of current transcript
  captionsEnabled?: boolean; // Whether captions are on
};

export const AIAssistantPanel: React.FC<AIAssistantPanelProps> = ({
  sessionDuration,
  summary,
  keyPoints,
  actionItems,
  questions,
  qaHistory = [],
  onAskQuestion,
  onExport,
  isProcessing = false,
  transcriptLength = 0,
  captionsEnabled = false,
}) => {
  const [questionInput, setQuestionInput] = useState('');
  const [localQaHistory, setLocalQaHistory] = useState<{ question: string; answer: string }[]>([]);
  const qaHistoryRef = React.useRef<HTMLDivElement>(null);

  // Sync local history with prop changes (when restored from localStorage)
  React.useEffect(() => {
    console.log(`[AI Panel] Parent has ${qaHistory.length} Q&A pairs, local has ${localQaHistory.length}`);
    if (qaHistory.length > 0 && localQaHistory.length === 0) {
      console.log(`[AI Panel] Restoring ${qaHistory.length} Q&A pairs from parent`);
      setLocalQaHistory(qaHistory);
    }
  }, [qaHistory, localQaHistory.length]); // Sync when parent history changes

  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  const handleAskQuestion = async () => {
    if (questionInput.trim() && onAskQuestion) {
      const question = questionInput;
      setQuestionInput('');
      
      // Get answer from AI
      const answer = await onAskQuestion(question);
      
      // Add to local history
      setLocalQaHistory(prev => [...prev, { question, answer }]);
      
      // Auto-scroll to show latest answer
      setTimeout(() => {
        if (qaHistoryRef.current) {
          qaHistoryRef.current.scrollTop = qaHistoryRef.current.scrollHeight;
        }
      }, 100);
    }
  };

  return (
    <div className="w-80 h-full bg-gradient-to-b from-[#1a0a2e] to-[#0f0515] border-r border-purple-500/20 flex flex-col text-white">
      {/* Header */}
      <div className="px-4 py-3 border-b border-purple-500/20 bg-gradient-to-r from-purple-900/30 to-indigo-900/30">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center">
            <Sparkles className="h-4 w-4 text-white" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-white">AI ASSISTANT</h3>
            <div className="flex items-center gap-1 text-xs text-purple-300">
              <Clock className="h-3 w-3" />
              <span>Session: {formatDuration(sessionDuration)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* AI Status & Progress Indicator */}
      <div className="px-4 py-3 bg-black/20 border-b border-purple-500/10 space-y-2">
        {/* Caption Status */}
        <div className="flex items-center justify-between text-xs">
          <div className="flex items-center gap-2">
            {captionsEnabled ? (
              <>
                <Mic className="h-3 w-3 text-emerald-400" />
                <span className="text-emerald-300 font-semibold">Captions ON</span>
              </>
            ) : (
              <>
                <MicOff className="h-3 w-3 text-amber-400" />
                <span className="text-amber-300 font-semibold">Captions OFF</span>
              </>
            )}
          </div>
          {isProcessing && (
            <div className="flex items-center gap-1 text-purple-300">
              <Activity className="h-3 w-3 animate-pulse" />
              <span className="text-[10px]">Analyzing...</span>
            </div>
          )}
        </div>

        {/* Progress Bar */}
        <div className="space-y-1">
          <div className="flex items-center justify-between text-[10px]">
            <span className="text-gray-400">Transcript Progress</span>
            <span className={`font-semibold ${
              transcriptLength >= 300 ? 'text-emerald-400' : 
              transcriptLength >= 100 ? 'text-amber-400' : 
              'text-gray-500'
            }`}>
              {transcriptLength} / 300 chars
            </span>
          </div>
          <div className="h-1.5 bg-black/40 rounded-full overflow-hidden">
            <div 
              className={`h-full transition-all duration-500 ${
                transcriptLength >= 300 ? 'bg-gradient-to-r from-emerald-500 to-green-400' : 
                transcriptLength >= 100 ? 'bg-gradient-to-r from-amber-500 to-yellow-400' : 
                'bg-gradient-to-r from-gray-600 to-gray-500'
              }`}
              style={{ width: `${Math.min((transcriptLength / 300) * 100, 100)}%` }}
            />
          </div>
          <div className="text-[9px] text-gray-500 italic">
            {transcriptLength < 100 ? '💡 Need 100+ chars to start analyzing' :
             transcriptLength < 300 ? '⚡ 300+ chars for full AI summary' :
             '✨ AI summary active!'}
          </div>
        </div>

        {/* Caption Reminder */}
        {!captionsEnabled && (
          <div className="text-[10px] text-amber-300 bg-amber-900/20 rounded px-2 py-1 border border-amber-500/20">
            💡 Turn on Captions to enable AI summary
          </div>
        )}
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-4">
        {/* Summary Section */}
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-xs font-semibold text-purple-300 uppercase tracking-wide">
            <FileText className="h-3 w-3" />
            <span>Summary</span>
          </div>
          <div className="bg-purple-900/20 rounded-lg p-3 border border-purple-500/20">
            {isProcessing ? (
              <div className="flex items-center gap-2 text-xs text-purple-300">
                <div className="animate-spin rounded-full h-3 w-3 border-2 border-purple-400 border-t-transparent" />
                <span>Analyzing...</span>
              </div>
            ) : summary ? (
              <p className="text-xs text-gray-300 leading-relaxed">{summary}</p>
            ) : (
              <p className="text-xs text-gray-500 italic">Summary will appear as discussion progresses...</p>
            )}
          </div>
        </div>

        {/* Key Points Section */}
        {keyPoints.length > 0 && (
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-xs font-semibold text-emerald-300 uppercase tracking-wide">
              <CheckSquare className="h-3 w-3" />
              <span>Key Points</span>
            </div>
            <div className="space-y-1.5">
              {keyPoints.map((point, index) => (
                <div
                  key={index}
                  className="flex items-start gap-2 text-xs text-gray-300 bg-emerald-900/10 rounded px-2 py-1.5 border border-emerald-500/20"
                >
                  <span className="text-emerald-400 font-bold mt-0.5">•</span>
                  <span className="flex-1">{point}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Action Items Section */}
        {actionItems.length > 0 && (
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-xs font-semibold text-amber-300 uppercase tracking-wide">
              <CheckSquare className="h-3 w-3" />
              <span>Action Items</span>
            </div>
            <div className="space-y-1.5">
              {actionItems.map((item, index) => (
                <div
                  key={index}
                  className="bg-amber-900/10 rounded px-2 py-1.5 border border-amber-500/20 text-xs"
                >
                  <div className="flex items-start gap-1.5">
                    <span className="text-amber-400 font-bold">✓</span>
                    <div className="flex-1">
                      <span className="text-amber-300 font-semibold">{item.owner}:</span>
                      <span className="text-gray-300 ml-1">{item.task}</span>
                      {item.deadline && (
                        <span className="text-amber-400 ml-1 text-[10px]">({item.deadline})</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Questions Section */}
        {questions.length > 0 && (
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-xs font-semibold text-cyan-300 uppercase tracking-wide">
              <HelpCircle className="h-3 w-3" />
              <span>Questions</span>
            </div>
            <div className="space-y-1.5">
              {questions.map((q, index) => (
                <div
                  key={index}
                  className="bg-cyan-900/10 rounded px-2 py-1.5 border border-cyan-500/20 text-xs"
                >
                  <div className="text-cyan-300">{q.text}</div>
                  <div className="text-gray-500 text-[10px] mt-0.5">— {q.askedBy}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Q&A Input Section */}
      <div className="border-t border-purple-500/20 bg-[#0f0515]/80 backdrop-blur-sm">
        {/* Q&A History Display */}
        {localQaHistory.length > 0 && (
          <div ref={qaHistoryRef} className="px-4 pt-3 max-h-40 overflow-y-auto space-y-2">
            {localQaHistory.slice(-3).map((qa, index) => (
              <div key={index} className="space-y-1">
                <div className="text-xs text-purple-300 font-semibold">Q: {qa.question}</div>
                <div className="text-xs text-gray-300 bg-purple-900/20 rounded px-2 py-1 border border-purple-500/20">
                  {qa.answer}
                </div>
              </div>
            ))}
          </div>
        )}
        
        <div className="px-4 py-3 space-y-2">
          <div className="flex items-center gap-2 text-xs font-semibold text-purple-300 uppercase tracking-wide">
            <Sparkles className="h-3 w-3" />
            <span>Ask AI</span>
          </div>
          <div className="flex gap-2">
            <textarea
              value={questionInput}
              onChange={(e) => setQuestionInput(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleAskQuestion();
                }
              }}
              placeholder="Type your question... (Shift+Enter for new line)"
              rows={2}
              className="flex-1 px-3 py-2 bg-purple-900/20 border border-purple-500/30 rounded text-xs text-white placeholder-gray-500 focus:outline-none focus:border-purple-400 focus:ring-1 focus:ring-purple-400 resize-none"
              disabled={isProcessing}
            />
            <button
              onClick={handleAskQuestion}
              disabled={!questionInput.trim() || isProcessing}
              className="px-3 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded text-xs font-semibold hover:from-purple-500 hover:to-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              Send
            </button>
          </div>
        </div>

        {/* Export Buttons */}
        <div className="px-4 pb-3 flex gap-2">
          <button
            onClick={() => onExport?.('txt')}
            className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-purple-900/20 border border-purple-500/30 rounded text-xs text-purple-300 hover:bg-purple-900/30 hover:border-purple-400 transition-all"
          >
            <Download className="h-3 w-3" />
            <span>TXT</span>
          </button>
          <button
            onClick={() => onExport?.('pdf')}
            className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-purple-900/20 border border-purple-500/30 rounded text-xs text-purple-300 hover:bg-purple-900/30 hover:border-purple-400 transition-all"
          >
            <Download className="h-3 w-3" />
            <span>PDF</span>
          </button>
          <button
            onClick={() => {/* TODO: Share functionality */}}
            className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-purple-900/20 border border-purple-500/30 rounded text-xs text-purple-300 hover:bg-purple-900/30 hover:border-purple-400 transition-all"
          >
            <Share2 className="h-3 w-3" />
            <span>Share</span>
          </button>
        </div>
      </div>
    </div>
  );
};
